/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @version 1.26.1
 * @since 1.25.0
 */

import { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import { useSendNewsItemViewedMutation } from '@/main/pages/PageNews/store';

import { makeSelectUserNotificationsTranslation } from '../../internationalization/intSelectors';
import { ENotificationsItemType, ENotificationsFeedbackCode, ENotificationUpdateStatus } from '../constants';
import { makeSelectUserNotificationsItems, makeSelectUserPushNotifications } from '../selectors';
import { setNotificationsShowed } from '../slice';
import {
    useSendNotificationFeedbackMutation,
    useShownNotificationMutation,
    useUpdateNotificationMutation,
} from '../store';
import {
    askUserPermissionForNotifications,
    createNewNotification,
    getIsGroupNotification,
    getIsNotificationsSupported,
} from '../utils/notificationsUtils';

export const usePushNotifications = () => {
    const dispatch = useDispatch();

    const notificationsTranslation = useSelector(makeSelectUserNotificationsTranslation());
    const pushNotifications = useSelector(makeSelectUserPushNotifications());
    const notifications = useSelector(makeSelectUserNotificationsItems());
    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());

    const [sendShown] = useShownNotificationMutation();

    const [sendNewsItemViewed] = useSendNewsItemViewedMutation();
    const [sendNotificationFeedback] = useSendNotificationFeedbackMutation();
    const [updateNotification] = useUpdateNotificationMutation();

    const notificationPermissionRef = useRef<boolean>(false);

    useEffect(() => {
        const isNotificationsSupported = getIsNotificationsSupported();
        if (isNotificationsSupported) {
            askUserPermissionForNotifications().then((data) => {
                if (data === 'granted') notificationPermissionRef.current = true;
            });
        }
    }, []);

    const handleExternalNewsItem = useCallback(
        (id: string | number) => {
            sendNewsItemViewed({ id });
        },
        [sendNewsItemViewed],
    );

    const handleExternalCvmsNotificationViewed = (id: number | string) => {
        sendNotificationFeedback({
            id: id,
            type: ENotificationsItemType.NOTIFICATION_CVM,
            feedbackCode: ENotificationsFeedbackCode.TARGET_ACTION_EXECUTED,
        })
    };

    const handleCashbackNotificationViewed = useCallback((id: string | number) => {
            updateNotification({
                id: id,
                isGroup: false,
                type: ENotificationsItemType.NOTIFICATION_CASHBACK,
                status: ENotificationUpdateStatus.VIEWED,
            });
        },
        [updateNotification],
    );

    useEffect(() => {
        if (notificationPermissionRef.current && pushNotifications.length) {
            // create push notifications
            pushNotifications.forEach((el) => {
                const pushNotification = createNewNotification(
                    el,
                    notificationsTranslation,
                    isServiceUser,
                    {
                        handleExternalNewsViewed: handleExternalNewsItem,
                        handleCvmNotificationViewed: handleExternalCvmsNotificationViewed,
                        handleCashbackNotificationViewed: handleCashbackNotificationViewed,
                    });
                if (pushNotification) {
                    pushNotification.onshow = () =>
                        sendShown({ id: el.id, isGroup: getIsGroupNotification(el.entity), type: el.type });
                }
            });
            // mark notifications in the slice as shown
            dispatch(setNotificationsShowed(pushNotifications.map((el) => el.id)));
        }
        // handle pushNotification only when all notification array is changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications, notificationsTranslation, sendShown]);
};
